import React, { useState } from 'react';
import PropTypes from 'prop-types';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import { getInternetAmenityPlanIds } from 'common/internetAmenityPlans';
import { SR_API_ACTION_UTILITY_ACTIVATION_TRACK_INTERNET_FLOW } from 'api/utilityActionTypes';
import {
  ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_FLOW_CONFIRM_ORDER,
  ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_FLOW_TRANSFER_CURRENT_PLAN,
} from 'client/analytics/eventList';
import { UTILITY_PROVIDER_PURCHASE_FLOW_TYPE_HYBRID } from 'common/utility/constants';

import ButtonDeprecated, { BUTTON_THEME_GREEN_ADOBE } from 'client/components/ButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrUtilitySetupTransferedAccountModal from 'client/components/SrUtilitySetup/SrUtilitySetupTransferedAccountModal';
import SrUtilityProfitProviderPlanCard, {
  PROVIDER_PLAN_CARD_THEME_DEFAULT,
  PROVIDER_PLAN_CARD_THEME_RETAIL_AMENITY,
} from 'client/components/SrUtilityProfitProviderPlanCard/SrUtilityProfitProviderPlanCard';

import './SrUtilitySetupProviderNewCustomerDeals.less';

const SrUtilitySetupProviderNewCustomerDeals = ({
  utilityProviderId,
  utilityPartner,
  utilityActivation,
  utilityActivationItem,
  utilityProviders,
  history,
  locationQuery,
}) => {
  const [utilityProviderPlanId, setUtilityProviderPlanId] = useState(
    utilityActivationItem.utilityProviderPlanId || locationQuery.id
      ? parseInt(locationQuery.id, 10)
      : null,
  );

  const [showTransferedAccountModal, setShowTransferedAccountModal] = useState(false);
  const dispatch = useDispatch();

  const trackInternetFlowEvent = (eventName) => {
    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_TRACK_INTERNET_FLOW,
      payload: {
        utilityActivationId: utilityActivation.id,
        internetFlowEventName: eventName,
      },
    });
  };

  const onShowTransferedAccountModal = () => {
    return trackInternetFlowEvent(ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_FLOW_TRANSFER_CURRENT_PLAN)
      .then(() => {
        setShowTransferedAccountModal(true);
      })
      .catch(() => {
        setShowTransferedAccountModal(true);
      });
  };

  const onContinue = () => {
    const link = [
      '/utility-setup',
      utilityPartner.slug,
      utilityActivation.shortId,
      utilityActivationItem.type.label.toLowerCase(),
      'plan',
      utilityProviderId,
      utilityProviderPlanId,
      '?newCustomerDeals=true',
    ].join('/');

    return trackInternetFlowEvent(ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_FLOW_CONFIRM_ORDER)
      .then(() => {
        history.push(link);
      })
      .catch(() => {
        history.push(link);
      });
  };

  const provider = utilityProviders.find(({ id }) => id === utilityProviderId);
  const internetAmenityPlanIds = getInternetAmenityPlanIds(utilityProviders);

  // No need to sort plans anymore - just use them as they are
  const sortedPlans = provider.plans;

  return (
    <div styleName="view">
      <div styleName="titleBox">
        <span>Transfer to new address</span>
      </div>
      <div styleName="subtitleBox">
        <span>Find your current internet speed below. Or pick an upgraded speed.</span>
      </div>
      <div styleName="contentBox">
        <div styleName="providerBox" key={provider.id}>
          <div>
            {provider.logoUrl ? (
              <SrBox styleName="providerLogo" backgroundImage={`url(${provider.logoUrl})`} />
            ) : (
              <span styleName="providerNameText">{provider.title}</span>
            )}
          </div>
          {provider.marketingDescription && (
            <div styleName="marketingDescriptionBox">
              <span>{provider.marketingDescription}</span>
            </div>
          )}
          <div styleName="chooseProviderPlanBox">
            <span>Choose a plan:</span>
          </div>
          <div styleName="providerPlansBox">
            {sortedPlans.map((plan) => {
              const isInternetAmenityPlan = internetAmenityPlanIds.has(plan.id);

              return (
                <SrUtilityProfitProviderPlanCard
                  key={plan.id}
                  onClick={() => {
                    setUtilityProviderPlanId(plan.id);
                  }}
                  isActive={plan.id === utilityProviderPlanId}
                  utilityProviderPlan={plan}
                  theme={
                    isInternetAmenityPlan &&
                    provider.purchaseFlowType === UTILITY_PROVIDER_PURCHASE_FLOW_TYPE_HYBRID
                      ? PROVIDER_PLAN_CARD_THEME_RETAIL_AMENITY
                      : PROVIDER_PLAN_CARD_THEME_DEFAULT
                  }
                  isDefaultInternetPlan={isInternetAmenityPlan}
                  utilityPartner={utilityPartner}
                />
              );
            })}
          </div>
        </div>
        <div styleName="transferredAccountBox">
          <span onClick={onShowTransferedAccountModal}>
            I already transferred my internet to the new address →
          </span>
        </div>
      </div>
      <SrStaticFooter>
        <ButtonDeprecated
          type="submit"
          theme={BUTTON_THEME_GREEN_ADOBE}
          disabled={!utilityProviderPlanId}
          onClick={onContinue}
        >
          Continue
        </ButtonDeprecated>
      </SrStaticFooter>
      {showTransferedAccountModal && (
        <SrUtilitySetupTransferedAccountModal
          utilityActivation={utilityActivation}
          utilityActivationItem={utilityActivationItem}
          onClose={() => setShowTransferedAccountModal(false)}
        />
      )}
    </div>
  );
};

SrUtilitySetupProviderNewCustomerDeals.displayName = 'SrUtilitySetupProviderNewCustomerDeals';

SrUtilitySetupProviderNewCustomerDeals.propTypes = {
  utilityPartner: PropTypes.object.isRequired,
  utilityActivation: PropTypes.object.isRequired,
  utilityActivationItem: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  locationQuery: PropTypes.object.isRequired,
};

export default SrUtilitySetupProviderNewCustomerDeals;
